import React, { useState, useEffect } from 'react';
import { 
  Key, Plus, Trash2, Copy, Check, Eye, EyeOff, 
  RefreshCw, Book, Code, Terminal, Activity,
  Clock, Shield, AlertTriangle
} from 'lucide-react';
import api from '../services/api';

const WebhooksAdmin = () => {
  const [apiKeys, setApiKeys] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDocModal, setShowDocModal] = useState(false);
  const [newKeyData, setNewKeyData] = useState({ name: '', description: '', permissions: ['read', 'write'] });
  const [createdKey, setCreatedKey] = useState(null);
  const [copiedKey, setCopiedKey] = useState(null);
  const [activeTab, setActiveTab] = useState('keys');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [keysRes, logsRes] = await Promise.all([
        api.get('/webhook/api-keys'),
        api.get('/webhook/logs?limit=20')
      ]);
      setApiKeys(keysRes.data);
      setLogs(logsRes.data);
    } catch (error) {
      console.error('Erreur:', error);
    } finally {
      setLoading(false);
    }
  };

  const createApiKey = async () => {
    try {
      const res = await api.post('/webhook/api-keys', newKeyData);
      setCreatedKey(res.data);
      await loadData();
      setNewKeyData({ name: '', description: '', permissions: ['read', 'write'] });
    } catch (error) {
      console.error('Erreur:', error);
      alert('Erreur lors de la création');
    }
  };

  const deleteApiKey = async (id) => {
    if (!window.confirm('Supprimer cette clé API ? Cette action est irréversible.')) return;
    try {
      await api.delete(`/webhook/api-keys/${id}`);
      await loadData();
    } catch (error) {
      console.error('Erreur:', error);
    }
  };

  const toggleApiKey = async (id) => {
    try {
      await api.patch(`/webhook/api-keys/${id}/toggle`);
      await loadData();
    } catch (error) {
      console.error('Erreur:', error);
    }
  };

  const copyToClipboard = (text, keyId) => {
    navigator.clipboard.writeText(text);
    setCopiedKey(keyId);
    setTimeout(() => setCopiedKey(null), 2000);
  };

  const baseUrl = process.env.REACT_APP_BACKEND_URL;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">🔗 API & Webhooks</h1>
          <p className="text-gray-600 mt-1">Gérez les intégrations avec vos partenaires</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => setShowDocModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition"
          >
            <Book className="w-4 h-4" />
            Documentation
          </button>
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
          >
            <Plus className="w-4 h-4" />
            Nouvelle clé API
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-xl p-5 text-white">
          <Key className="w-8 h-8 mb-2 opacity-80" />
          <div className="text-3xl font-bold">{apiKeys.length}</div>
          <div className="text-indigo-100 text-sm">Clés API</div>
        </div>
        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-5 text-white">
          <Shield className="w-8 h-8 mb-2 opacity-80" />
          <div className="text-3xl font-bold">{apiKeys.filter(k => k.actif).length}</div>
          <div className="text-green-100 text-sm">Clés actives</div>
        </div>
        <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-5 text-white">
          <Activity className="w-8 h-8 mb-2 opacity-80" />
          <div className="text-3xl font-bold">{apiKeys.reduce((sum, k) => sum + k.requests_count, 0)}</div>
          <div className="text-purple-100 text-sm">Total requêtes</div>
        </div>
        <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl p-5 text-white">
          <Clock className="w-8 h-8 mb-2 opacity-80" />
          <div className="text-3xl font-bold">{logs.length}</div>
          <div className="text-orange-100 text-sm">Logs récents</div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex gap-8">
          <button
            onClick={() => setActiveTab('keys')}
            className={`pb-4 px-1 border-b-2 font-medium transition ${
              activeTab === 'keys' 
                ? 'border-green-600 text-green-600' 
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            🔑 Clés API
          </button>
          <button
            onClick={() => setActiveTab('logs')}
            className={`pb-4 px-1 border-b-2 font-medium transition ${
              activeTab === 'logs' 
                ? 'border-green-600 text-green-600' 
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            📋 Logs
          </button>
        </nav>
      </div>

      {/* Content */}
      {activeTab === 'keys' && (
        <div className="space-y-4">
          {apiKeys.length === 0 ? (
            <div className="text-center py-12 bg-gray-50 rounded-xl">
              <Key className="w-12 h-12 mx-auto text-gray-400 mb-4" />
              <p className="text-gray-600">Aucune clé API créée</p>
              <button
                onClick={() => setShowCreateModal(true)}
                className="mt-4 text-green-600 hover:underline"
              >
                Créer votre première clé
              </button>
            </div>
          ) : (
            apiKeys.map((key) => (
              <div key={key.id} className={`bg-white rounded-xl border-2 p-6 ${key.actif ? 'border-green-200' : 'border-gray-200'}`}>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3">
                      <h3 className="text-lg font-bold text-gray-900">{key.name}</h3>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                        key.actif ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                      }`}>
                        {key.actif ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                    {key.description && (
                      <p className="text-gray-600 text-sm mt-1">{key.description}</p>
                    )}
                    
                    <div className="flex items-center gap-2 mt-3 font-mono text-sm bg-gray-100 rounded-lg px-3 py-2">
                      <code className="text-gray-700">{key.key_preview}</code>
                      <button
                        onClick={() => copyToClipboard(key.key_preview, key.id)}
                        className="text-gray-500 hover:text-gray-700"
                        title="Copier"
                      >
                        {copiedKey === key.id ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
                      </button>
                    </div>

                    <div className="flex items-center gap-6 mt-4 text-sm text-gray-500">
                      <span className="flex items-center gap-1">
                        <Activity className="w-4 h-4" />
                        {key.requests_count} requêtes
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        {key.last_used ? new Date(key.last_used).toLocaleString('fr-FR') : 'Jamais utilisée'}
                      </span>
                      <span className="flex items-center gap-1">
                        Permissions: {key.permissions.join(', ')}
                      </span>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => toggleApiKey(key.id)}
                      className={`px-3 py-1 rounded-lg text-sm font-medium transition ${
                        key.actif 
                          ? 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200' 
                          : 'bg-green-100 text-green-700 hover:bg-green-200'
                      }`}
                    >
                      {key.actif ? 'Désactiver' : 'Activer'}
                    </button>
                    <button
                      onClick={() => deleteApiKey(key.id)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {activeTab === 'logs' && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Partenaire</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Méthode</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Endpoint</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Statut</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {logs.map((log) => (
                  <tr key={log.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {new Date(log.created_at).toLocaleString('fr-FR')}
                    </td>
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">{log.api_key_name}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded text-xs font-semibold ${
                        log.method === 'GET' ? 'bg-blue-100 text-blue-700' :
                        log.method === 'POST' ? 'bg-green-100 text-green-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        {log.method}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm font-mono text-gray-600">{log.endpoint}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded text-xs font-semibold ${
                        log.status_code === 200 ? 'bg-green-100 text-green-700' :
                        log.status_code >= 400 ? 'bg-red-100 text-red-700' :
                        'bg-yellow-100 text-yellow-700'
                      }`}>
                        {log.status_code}
                      </span>
                    </td>
                  </tr>
                ))}
                {logs.length === 0 && (
                  <tr>
                    <td colSpan="5" className="px-4 py-8 text-center text-gray-500">
                      Aucun log disponible
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Modal Création */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6">
            {!createdKey ? (
              <>
                <h2 className="text-2xl font-bold text-gray-900 mb-6">🔑 Nouvelle clé API</h2>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Nom du partenaire *</label>
                    <input
                      type="text"
                      value={newKeyData.name}
                      onChange={(e) => setNewKeyData({ ...newKeyData, name: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                      placeholder="Ex: CRM Partenaire X"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                    <textarea
                      value={newKeyData.description}
                      onChange={(e) => setNewKeyData({ ...newKeyData, description: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                      rows="2"
                      placeholder="Description de l'usage..."
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Permissions</label>
                    <div className="flex gap-4">
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={newKeyData.permissions.includes('read')}
                          onChange={(e) => {
                            const perms = e.target.checked 
                              ? [...newKeyData.permissions, 'read']
                              : newKeyData.permissions.filter(p => p !== 'read');
                            setNewKeyData({ ...newKeyData, permissions: perms });
                          }}
                          className="rounded text-green-600"
                        />
                        Lecture (GET)
                      </label>
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={newKeyData.permissions.includes('write')}
                          onChange={(e) => {
                            const perms = e.target.checked 
                              ? [...newKeyData.permissions, 'write']
                              : newKeyData.permissions.filter(p => p !== 'write');
                            setNewKeyData({ ...newKeyData, permissions: perms });
                          }}
                          className="rounded text-green-600"
                        />
                        Écriture (POST)
                      </label>
                    </div>
                  </div>
                </div>
                <div className="flex gap-3 mt-6">
                  <button
                    onClick={() => {
                      setShowCreateModal(false);
                      setCreatedKey(null);
                    }}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg font-semibold text-gray-700 hover:bg-gray-50"
                  >
                    Annuler
                  </button>
                  <button
                    onClick={createApiKey}
                    disabled={!newKeyData.name}
                    className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 disabled:opacity-50"
                  >
                    Créer
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="text-center">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Check className="w-8 h-8 text-green-600" />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">Clé API créée !</h2>
                  <p className="text-gray-600 mb-6">Copiez cette clé maintenant, elle ne sera plus visible après.</p>
                </div>
                
                <div className="bg-gray-900 rounded-lg p-4 mb-6">
                  <div className="flex items-center justify-between">
                    <code className="text-green-400 text-sm break-all">{createdKey.key}</code>
                    <button
                      onClick={() => copyToClipboard(createdKey.key, 'created')}
                      className="ml-2 text-gray-400 hover:text-white"
                    >
                      {copiedKey === 'created' ? <Check className="w-5 h-5 text-green-400" /> : <Copy className="w-5 h-5" />}
                    </button>
                  </div>
                </div>
                
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
                  <div className="flex gap-2">
                    <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0" />
                    <p className="text-sm text-yellow-800">
                      Cette clé ne sera plus affichée. Conservez-la dans un endroit sécurisé.
                    </p>
                  </div>
                </div>
                
                <button
                  onClick={() => {
                    setShowCreateModal(false);
                    setCreatedKey(null);
                  }}
                  className="w-full px-4 py-2 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700"
                >
                  J'ai copié ma clé
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* Modal Documentation */}
      {showDocModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-900">📚 Documentation API SocialAC</h2>
              <button
                onClick={() => setShowDocModal(false)}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                ✕
              </button>
            </div>
            
            <div className="p-6 prose prose-sm max-w-none">
              <h3>🔐 Authentification</h3>
              <p>Toutes les requêtes doivent inclure votre clé API dans le header :</p>
              <pre className="bg-gray-900 text-green-400 p-4 rounded-lg overflow-x-auto">
{`X-API-Key: sk_live_votre_cle_api`}
              </pre>

              <h3>📍 URL de base</h3>
              <pre className="bg-gray-100 p-3 rounded-lg">{baseUrl}/api/webhook</pre>

              <hr />

              <h3>📥 Créer un dossier</h3>
              <p><strong>POST</strong> <code>/dossiers</code></p>
              <pre className="bg-gray-900 text-green-400 p-4 rounded-lg overflow-x-auto">
{`curl -X POST "${baseUrl}/api/webhook/dossiers" \\
  -H "X-API-Key: sk_live_votre_cle" \\
  -H "Content-Type: application/json" \\
  -d '{
    "reference_externe": "CRM-12345",
    "nom": "DUPONT",
    "prenom": "Marie",
    "adresse": "12 rue des Lilas",
    "code_postal": "75001",
    "ville": "Paris",
    "telephone": "0612345678",
    "email": "marie.dupont@email.com",
    "caisse": "AG2R La Mondiale",
    "motif": "Aide au logement",
    "priorite": "med"
  }'`}
              </pre>
              <p><strong>Réponse :</strong></p>
              <pre className="bg-gray-100 p-4 rounded-lg">
{`{
  "success": true,
  "dossier_id": "uuid-du-dossier",
  "reference": "API-2026-0001",
  "message": "Dossier créé avec succès"
}`}
              </pre>

              <hr />

              <h3>📤 Récupérer le statut d'un dossier</h3>
              <p><strong>GET</strong> <code>/dossiers/{'{reference}'}</code></p>
              <pre className="bg-gray-900 text-green-400 p-4 rounded-lg overflow-x-auto">
{`curl -X GET "${baseUrl}/api/webhook/dossiers/API-2026-0001" \\
  -H "X-API-Key: sk_live_votre_cle"`}
              </pre>
              <p><strong>Réponse :</strong></p>
              <pre className="bg-gray-100 p-4 rounded-lg">
{`{
  "reference": "API-2026-0001",
  "reference_externe": "CRM-12345",
  "statut": "inprog",
  "statut_label": "En cours",
  "priorite": "med",
  "date_creation": "2026-03-10T12:00:00",
  "gestionnaire": "Sophie Martin"
}`}
              </pre>

              <hr />

              <h3>💬 Récupérer les commentaires</h3>
              <p><strong>GET</strong> <code>/dossiers/{'{reference}'}/commentaires</code></p>
              <pre className="bg-gray-900 text-green-400 p-4 rounded-lg overflow-x-auto">
{`curl -X GET "${baseUrl}/api/webhook/dossiers/API-2026-0001/commentaires" \\
  -H "X-API-Key: sk_live_votre_cle"`}
              </pre>

              <hr />

              <h3>✍️ Ajouter un commentaire</h3>
              <p><strong>POST</strong> <code>/dossiers/{'{reference}'}/commentaires</code></p>
              <pre className="bg-gray-900 text-green-400 p-4 rounded-lg overflow-x-auto">
{`curl -X POST "${baseUrl}/api/webhook/dossiers/API-2026-0001/commentaires" \\
  -H "X-API-Key: sk_live_votre_cle" \\
  -H "Content-Type: application/json" \\
  -d '{
    "message": "Information complémentaire du partenaire",
    "auteur": "CRM Partenaire"
  }'`}
              </pre>

              <hr />

              <h3>📋 Lister les dossiers</h3>
              <p><strong>GET</strong> <code>/dossiers?statut=new&limit=50&offset=0</code></p>
              <pre className="bg-gray-900 text-green-400 p-4 rounded-lg overflow-x-auto">
{`curl -X GET "${baseUrl}/api/webhook/dossiers?statut=inprog&limit=10" \\
  -H "X-API-Key: sk_live_votre_cle"`}
              </pre>

              <hr />

              <h3>📊 Codes de statut</h3>
              <table className="w-full">
                <thead>
                  <tr>
                    <th className="text-left">Code</th>
                    <th className="text-left">Label</th>
                    <th className="text-left">Description</th>
                  </tr>
                </thead>
                <tbody>
                  <tr><td><code>new</code></td><td>Nouveau</td><td>Dossier créé, non traité</td></tr>
                  <tr><td><code>inprog</code></td><td>En cours</td><td>Dossier en traitement</td></tr>
                  <tr><td><code>done</code></td><td>Clôturé</td><td>Dossier traité avec succès</td></tr>
                  <tr><td><code>reject</code></td><td>Refusé</td><td>Dossier refusé</td></tr>
                </tbody>
              </table>

              <hr />

              <h3>⚠️ Codes d'erreur</h3>
              <table className="w-full">
                <thead>
                  <tr>
                    <th className="text-left">Code HTTP</th>
                    <th className="text-left">Description</th>
                  </tr>
                </thead>
                <tbody>
                  <tr><td>401</td><td>Clé API manquante ou invalide</td></tr>
                  <tr><td>403</td><td>Permission insuffisante</td></tr>
                  <tr><td>404</td><td>Dossier non trouvé</td></tr>
                  <tr><td>422</td><td>Données invalides</td></tr>
                  <tr><td>429</td><td>Limite de requêtes dépassée</td></tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WebhooksAdmin;
